import { useEffect, useState } from "react";
import dayjs from "dayjs";
import { Switch } from "@material-tailwind/react";
import {
  FormInput,
  FormSelect,
  PrimaryButton,
  RedButton,
} from "../../components";
import { usePartners } from "../../store/usePartners";
import { IVoucher, useVouchers } from "../../store/useVouchers";
import { useAlert } from "../../store/useAlert";
import { withProtectedRoute } from "../../utils/HOC/withProtectedRoute";
import { withAdminPermission } from "../../utils/HOC/withAdminPermission";

const PAGE_SIZE = 25;

// Starting-point copy for the two instruction "flavors" - pilot phase, all
// manual input, not modelled as a real voucher type (see IKEA handoff doc).
// Reviewed/adjust as needed; these are just an insert-then-edit starting point.
const TEMPLATES = {
  discount: {
    en: "Thank you for returning your old IKEA items! To redeem, first scan your IKEA Family card and redeem the voucher in a single transaction. Valid in-store and online. The Swedish Restaurant, the Bistro, and the Swedish Delicatessen are excluded.",
    nl: "Bedankt voor het inleveren van je oude IKEA artikelen! Activeer door eerst je IKEA Family-kaart te scannen en verzilver de voucher in één transactie. Geldig in de winkel en online. Het Zweedse Restaurant, de Bistro of de Zweedse Delicatessen zijn uitgesloten.",
  },
  breakfast: {
    en: "Thank you for recycling with us! As a thank you, please enjoy this complimentary breakfast voucher - show this code at the IKEA restaurant.",
    nl: "Bedankt voor je deelname! Als bedankje ontvang je dit gratis ontbijtvoucher - toon deze code in het IKEA restaurant.",
  },
};

// Shown in the app's voucher list in the orange block above the value, in the
// user's app language, so a discount voucher can be told apart from a
// breakfast one. Texts are a starting point - final wording TBD by product.
const PRIZE_NAME_TEMPLATES = {
  discount: { en: "Discount", nl: "Korting" },
  breakfast: { en: "Breakfast worth", nl: "Ontbijt t.w.v." },
};

const getDropField = (voucher: IVoucher, field: string): any => {
  if (voucher.drop && typeof voucher.drop === "object") {
    return (voucher.drop as Record<string, any>)[field];
  }
  return undefined;
};

const getCustomerLabel = (voucher: IVoucher): string => {
  const user = Array.isArray(voucher.redeemedBy) ? voucher.redeemedBy[0] : null;
  if (user && typeof user === "object") {
    const name = [user.first_name, user.last_name].filter(Boolean).join(" ");
    return name || user.email || "-";
  }
  return "-";
};

// "-" instead of literally rendering "€NaN" when the source value isn't
// actually numeric.
const formatEuro = (n: number): string =>
  Number.isFinite(n) ? `€${n.toFixed(2)}` : "-";

function VoucherEditModal({
  voucher,
  onClose,
}: {
  voucher: IVoucher;
  onClose: () => void;
}) {
  const { assignVoucherCode } = useVouchers();
  const { setAlert } = useAlert();
  const partnerData = getDropField(voucher, "partnerData");
  const location = getDropField(voucher, "location");
  const hasCode = Boolean(voucher.code);

  const [code, setCode] = useState(voucher.code ?? "");
  const [value, setValue] = useState(
    // Only prefill a real positive amount. A drop-time estimate of 0 (e.g. a
    // breakfast voucher) must show as empty, not as "0", which the save
    // validation would then reject as not-positive.
    Number(voucher.value) > 0 ? String(Number(voucher.value) / 100) : ""
  );
  const [expireDate, setExpireDate] = useState(
    voucher.expireDate ? dayjs(voucher.expireDate).format("YYYY-MM-DD") : ""
  );
  const [instructionsEn, setInstructionsEn] = useState(
    voucher.instructionsEn ?? ""
  );
  const [instructionsNl, setInstructionsNl] = useState(
    voucher.instructionsNl ?? ""
  );
  const [prizeNameEn, setPrizeNameEn] = useState(voucher.prizeNameEn ?? "");
  const [prizeNameNl, setPrizeNameNl] = useState(voucher.prizeNameNl ?? "");
  // Of the total items, how many were rejected — 0 means all accepted.
  // Replaces the old meetsRequirements boolean, which couldn't represent a
  // partially-accepted drop.
  const [numberOfRejected, setNumberOfRejected] = useState<string>(
    String(partnerData?.numberOfRejected ?? 0)
  );
  const totalItems = Number(partnerData?.numberOfItems ?? 0);
  const [isSaving, setIsSaving] = useState(false);

  const minExpireDate = dayjs().add(1, "day").format("YYYY-MM-DD");

  const handleSave = async () => {
    // Name only the field that is actually missing: the old combined message
    // listed both, so leaving just the code empty read as an expiry date
    // problem even though the date was fine.
    const missing = [
      !code.trim() && "the voucher code",
      !expireDate && "the expiry date",
    ].filter(Boolean);
    if (missing.length) {
      setAlert({
        isOpen: true,
        message: `Enter ${missing.join(" and ")}.`,
        severity: "error",
      });
      return;
    }
    // Optional: an empty value is deliberate (breakfast vouchers carry no
    // amount) and clears the drop-time estimate, so the app shows only the
    // prize name. When filled in, it still has to be a positive amount.
    const trimmedValue = value.trim();
    const numericValue = trimmedValue
      ? Number(trimmedValue.replace(/,/g, "."))
      : undefined;
    if (
      trimmedValue &&
      (!Number.isFinite(numericValue) || (numericValue as number) <= 0)
    ) {
      setAlert({
        isOpen: true,
        message: "The voucher value must be a positive amount in euros.",
        severity: "error",
      });
      return;
    }
    if (dayjs(expireDate).isBefore(dayjs(minExpireDate))) {
      setAlert({
        isOpen: true,
        message: "The expiry date must be in the future.",
        severity: "error",
      });
      return;
    }
    const rejectedCount = Number(numberOfRejected);
    if (
      !Number.isFinite(rejectedCount) ||
      rejectedCount < 0 ||
      (totalItems > 0 && rejectedCount > totalItems)
    ) {
      setAlert({
        isOpen: true,
        message: "Number of rejected items cannot exceed the total number of items.",
        severity: "error",
      });
      return;
    }

    setIsSaving(true);
    try {
      await assignVoucherCode(
        voucher._id as string,
        code.trim(),
        expireDate,
        numericValue,
        // Empty rather than undefined, so clearing a text actually clears it
        // instead of leaving the key out and keeping the old one.
        instructionsEn ?? "",
        instructionsNl ?? "",
        prizeNameEn.trim() || undefined,
        prizeNameNl.trim() || undefined,
        rejectedCount
      );
      setAlert({
        isOpen: true,
        message: "Saved - the customer has been notified.",
        severity: "success",
      });
      onClose();
    } catch (error: any) {
      setAlert({
        isOpen: true,
        message:
          error?.response?.data?.message ?? "Saving failed. Please try again.",
        severity: "error",
      });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <>
      <div className="justify-center items-center z-[10000000000] backdrop-blur-sm flex overflow-x-hidden overflow-y-auto fixed inset-0 outline-none focus:outline-none">
        <div className="relative w-11/12 sm:w-2/3 my-6 mx-auto max-w-2xl">
          <div className="border-0 rounded-lg shadow-lg relative flex flex-col max-h-[85vh] bg-white overflow-y-auto w-full outline-none focus:outline-none">
            <div className="flex items-start justify-between p-5 border-b border-solid border-blueGray-200 rounded-t">
              <div>
                <h3 className="text-xl font-semibold font-satoshi text-dark">
                  {hasCode ? "Edit voucher" : "Assign voucher code"}
                </h3>
                <p className="text-sm text-gray-600 mt-1">
                  {typeof voucher.partnerId === "object" &&
                    (voucher.partnerId as any)
                    ? (voucher.partnerId as any)?.name
                    : "Partner"}{" "}
                  &middot; {getCustomerLabel(voucher)}
                </p>
              </div>
              <button
                className="text-gray-500"
                onClick={onClose}
                disabled={isSaving}
              >
                ✕
              </button>
            </div>

            <div className="relative p-6 flex-auto space-y-4">
              <div className="bg-gray-50 rounded-md p-4 grid grid-cols-2 gap-3 text-sm">
                <div>
                  <p className="text-gray-500 text-xs">Family member no.</p>
                  <p className="font-mono text-dark">
                    {partnerData?.ikeaFamilyMemberNumber ?? "-"}
                  </p>
                </div>
                <div>
                  <p className="text-gray-500 text-xs">Email</p>
                  <p className="text-dark truncate">
                    {partnerData?.emailAddress ?? "-"}
                  </p>
                </div>
                <div>
                  <p className="text-gray-500 text-xs">Items</p>
                  <p className="text-dark">
                    {partnerData?.numberOfItems ?? "-"}
                  </p>
                </div>
                <div>
                  <p className="text-gray-500 text-xs">Location</p>
                  <p className="text-dark">
                    {location && typeof location === "object"
                      ? (location.name ?? "-")
                      : "-"}
                  </p>
                </div>
                <div>
                  <p className="text-gray-500 text-xs">Requested value</p>
                  <p className="text-dark">
                    {partnerData?.voucherValue !== undefined
                      ? formatEuro(Number(partnerData.voucherValue))
                      : "-"}
                  </p>
                </div>
                <div>
                  <p className="text-gray-500 text-xs">Issued</p>
                  <p className="text-dark">
                    {voucher.createdAt
                      ? dayjs(voucher.createdAt).format("DD/MM/YYYY")
                      : "-"}
                  </p>
                </div>
              </div>

              <div>
                <FormInput
                  label={`Number of rejected items (out of ${totalItems || "?"} total)`}
                  placeholder="0"
                  value={numberOfRejected}
                  onChange={(e) => setNumberOfRejected(e.target.value)}
                  containerClassname="!mb-1"
                />
                <p className="text-xs text-gray-500">
                  Set by the employee at drop time. 0 = all accepted (regular
                  voucher). Equal to the total = none accepted (breakfast
                  voucher) - decide which instructions template to use based
                  on this. Correct it here if needed.
                </p>
              </div>

              <FormInput
                label="Voucher code *"
                placeholder="Voucher code"
                value={code}
                onChange={(e) => setCode(e.target.value)}
              />
              <div className="grid grid-cols-2 gap-4">
                <FormInput
                  label="Voucher value (€)"
                  placeholder="Empty = no value shown in the app"
                  value={value}
                  onChange={(e) => setValue(e.target.value)}
                />
                <FormInput
                  type="date"
                  label="Expiry date *"
                  value={expireDate}
                  min={minExpireDate}
                  onChange={(e) => setExpireDate(e.target.value)}
                />
              </div>

              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-sm font-medium text-gray-700">
                    Prize name (English)
                  </label>
                  <div className="flex gap-2">
                    <button
                      className="text-xs text-droppie-oranje font-medium"
                      onClick={() =>
                        setPrizeNameEn(PRIZE_NAME_TEMPLATES.discount.en)
                      }
                    >
                      Insert discount text
                    </button>
                    <button
                      className="text-xs text-droppie-oranje font-medium"
                      onClick={() =>
                        setPrizeNameEn(PRIZE_NAME_TEMPLATES.breakfast.en)
                      }
                    >
                      Insert breakfast text
                    </button>
                  </div>
                </div>
                <FormInput
                  placeholder="e.g. Discount voucher"
                  value={prizeNameEn}
                  onChange={(e) => setPrizeNameEn(e.target.value)}
                  containerClassname="!mb-1"
                />
                <p className="text-xs text-gray-500">
                  Shown in the app's voucher list above the value, so the
                  customer can tell a discount voucher from a breakfast one.
                </p>
              </div>

              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-sm font-medium text-gray-700">
                    Prize name (Dutch)
                  </label>
                  <div className="flex gap-2">
                    <button
                      className="text-xs text-droppie-oranje font-medium"
                      onClick={() =>
                        setPrizeNameNl(PRIZE_NAME_TEMPLATES.discount.nl)
                      }
                    >
                      Insert discount text
                    </button>
                    <button
                      className="text-xs text-droppie-oranje font-medium"
                      onClick={() =>
                        setPrizeNameNl(PRIZE_NAME_TEMPLATES.breakfast.nl)
                      }
                    >
                      Insert breakfast text
                    </button>
                  </div>
                </div>
                <FormInput
                  placeholder="bijv. Kortingsvoucher"
                  value={prizeNameNl}
                  onChange={(e) => setPrizeNameNl(e.target.value)}
                  containerClassname="!mb-1"
                />
              </div>

              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-sm font-medium text-gray-700">
                    Instructions (English)
                  </label>
                  <div className="flex gap-2">
                    <button
                      className="text-xs text-droppie-oranje font-medium"
                      onClick={() => setInstructionsEn(TEMPLATES.discount.en)}
                    >
                      Insert discount text
                    </button>
                    <button
                      className="text-xs text-droppie-oranje font-medium"
                      onClick={() => setInstructionsEn(TEMPLATES.breakfast.en)}
                    >
                      Insert breakfast text
                    </button>
                  </div>
                </div>
                <textarea
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  rows={3}
                  placeholder="Instructions shown to the customer (English)"
                  value={instructionsEn}
                  onChange={(e) => setInstructionsEn(e.target.value)}
                />
              </div>

              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-sm font-medium text-gray-700">
                    Instructions (Dutch)
                  </label>
                  <div className="flex gap-2">
                    <button
                      className="text-xs text-droppie-oranje font-medium"
                      onClick={() => setInstructionsNl(TEMPLATES.discount.nl)}
                    >
                      Insert discount text
                    </button>
                    <button
                      className="text-xs text-droppie-oranje font-medium"
                      onClick={() => setInstructionsNl(TEMPLATES.breakfast.nl)}
                    >
                      Insert breakfast text
                    </button>
                  </div>
                </div>
                <textarea
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  rows={3}
                  placeholder="Instructies voor de klant (Nederlands)"
                  value={instructionsNl}
                  onChange={(e) => setInstructionsNl(e.target.value)}
                />
              </div>
            </div>

            <div className="flex items-center justify-end p-6 border-t border-solid border-blueGray-200 rounded-b">
              <RedButton label="Cancel" className="mr-4" onClick={onClose} />
              <PrimaryButton
                label={isSaving ? "Saving..." : hasCode ? "Update" : "Save"}
                onClick={handleSave}
                disabled={isSaving}
              />
            </div>
          </div>
        </div>
      </div>
      <div className="opacity-25 fixed inset-0 z-40 bg-black" />
    </>
  );
}

/**
 * Backoffice queue of drop-issued partner vouchers. By default shows only
 * vouchers still waiting for a code; "Show already-assigned" also lists
 * coded ones, so a mistake (wrong code, value, or instructions) can be
 * corrected - everything here is manual input, so edits are expected, not
 * an edge case. Editing (via VoucherEditModal) is scoped server-side to
 * sourceType: DROP vouchers only, so this can never touch a
 * CHALLENGE/LOTTERY voucher.
 */
function PendingVoucherCodesScreen() {
  const { allPartners, getPartnersForSelect } = usePartners();
  const { pendingVouchers, hasNextPendingPage, getPendingVouchers } =
    useVouchers();

  const [selectedPartnerId, setSelectedPartnerId] = useState<string>("");
  const [includeAssigned, setIncludeAssigned] = useState(false);
  const [page, setPage] = useState<number>(1);
  const [editingVoucher, setEditingVoucher] = useState<IVoucher | null>(null);

  useEffect(() => {
    getPartnersForSelect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    getPendingVouchers(
      page,
      PAGE_SIZE,
      selectedPartnerId || undefined,
      includeAssigned
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPartnerId, includeAssigned, page]);

  useEffect(() => {
    setPage(1);
  }, [selectedPartnerId, includeAssigned]);

  const selectedPartnerName =
    allPartners.find((p) => p._id === selectedPartnerId)?.name ??
    "All partners";

  return (
    <div>
      {editingVoucher && (
        <VoucherEditModal
          voucher={editingVoucher}
          onClose={() => {
            setEditingVoucher(null);
            // Refresh the current page under the current filters so a saved
            // row correctly disappears from (or stays out of) the pending-
            // only view once it's coded.
            getPendingVouchers(
              page,
              PAGE_SIZE,
              selectedPartnerId || undefined,
              includeAssigned
            );
          }}
        />
      )}

      <div className="flex flex-row items-center justify-between mb-6">
        <div>
          <h1 className="font-satoshi text-2xl font-bold text-dark">
            Pending voucher codes
          </h1>
          <p className="text-sm text-gray-600 mt-1 max-w-2xl">
            Vouchers issued for partner-stream drops. Click a row to assign or
            edit its code, value, and instructions.
          </p>
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-none drop-shadow-none border-gray-border mb-4">
        <div className="w-full border-b border-gray-border px-6 py-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <p className="font-satoshi font-medium text-base text-dark">
              Partner selection
            </p>
            <p className="text-xs text-gray-600 mt-1">
              Choose a partner to filter the queue, or leave it empty to see all
              pending vouchers.
            </p>
          </div>
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
            <Switch
              crossOrigin={"anonymous"}
              color="teal"
              ripple
              label="Show already-assigned too"
              checked={includeAssigned}
              onChange={(e) => setIncludeAssigned(e.target.checked)}
            />
            <div className="min-w-[220px]">
              <FormSelect
                value={selectedPartnerId}
                onChange={(e: any) => setSelectedPartnerId(e.target.value)}
                options={[
                  { value: "", label: "All partners" },
                  ...allPartners.map((partner) => ({
                    value: partner._id as string,
                    label: partner.name as string,
                  })),
                ]}
                placeholder="All partners"
              />
            </div>
          </div>
        </div>

        <div className="w-full px-6 py-4">
          {!pendingVouchers.length ? (
            <p className="text-sm text-gray-600">
              {selectedPartnerId ? (
                <>
                  There are no {includeAssigned ? "" : "pending "}voucher codes
                  for <strong>{selectedPartnerName}</strong>.
                </>
              ) : (
                `There are no ${includeAssigned ? "" : "pending "}voucher codes.`
              )}
            </p>
          ) : (
            <div className="relative overflow-x-auto">
              <table className="w-full text-base font-normal font-satoshi text-left rtl:text-right text-dark">
                <thead className="font-medium bg-gray-50">
                  <tr>
                    <th className="px-2 sm:px-4 py-2 sm:py-3">Issued</th>
                    <th className="px-2 sm:px-4 py-2 sm:py-3">Partner</th>
                    <th className="px-2 sm:px-4 py-2 sm:py-3">Customer</th>
                    <th
                      className="px-2 min-w-[160px] sm:px-4 py-2 sm:py-3"
                      title="Voucher value the store employee indicated with the drop"
                    >
                      Requested value
                    </th>
                    <th
                      className="hidden min-w-[170px] sm:table-cell px-2 sm:px-4 py-2 sm:py-3"
                      title="Set by the employee at drop time"
                    >
                      Accepted items
                    </th>
                    <th className="px-2 sm:px-4 py-2 sm:py-3">Status</th>
                    <th className="px-2 sm:px-4 py-2 sm:py-3 text-right">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {pendingVouchers.map((voucher) => {
                    const id = voucher._id as string;
                    const partnerData = getDropField(voucher, "partnerData");
                    const hasCode = Boolean(voucher.code);
                    return (
                      <tr
                        key={id}
                        onClick={() => setEditingVoucher(voucher)}
                        className="bg-white hover:bg-gray-100 transition ease-linear border-b cursor-pointer"
                      >
                        <td className="px-2 sm:px-4 py-2 sm:py-3 whitespace-nowrap">
                          {voucher.createdAt
                            ? dayjs(voucher.createdAt).format("DD/MM/YYYY")
                            : "-"}
                        </td>
                        <td className="px-2 sm:px-4 py-2 sm:py-3 whitespace-nowrap">
                          {typeof voucher.partnerId === "object" &&
                            (voucher.partnerId as any)
                            ? ((voucher.partnerId as any)?.name ?? "-")
                            : "-"}
                        </td>
                        <td className="px-2 sm:px-4 py-2 sm:py-3 whitespace-nowrap">
                          {getCustomerLabel(voucher)}
                        </td>
                        <td className="px-2 sm:px-4 py-2 sm:py-3 whitespace-nowrap font-medium">
                          {partnerData?.voucherValue !== undefined
                            ? formatEuro(Number(partnerData.voucherValue))
                            : voucher.value
                              ? formatEuro(Number(voucher.value) / 100)
                              : "-"}
                        </td>
                        <td className="hidden sm:table-cell px-2 sm:px-4 py-2 sm:py-3">
                          {(() => {
                            const totalItems = Number(
                              partnerData?.numberOfItems ?? 0
                            );
                            const rejected = Number(
                              partnerData?.numberOfRejected ?? 0
                            );
                            const accepted = Math.max(0, totalItems - rejected);
                            return (
                              <span
                                className={`inline-flex items-center rounded-full text-xs font-medium px-2 py-0.5 ${rejected === 0
                                    ? "bg-green-100 text-green-700"
                                    : "bg-red-100 text-red-700"
                                  }`}
                              >
                                {accepted}/{totalItems}
                              </span>
                            );
                          })()}
                        </td>
                        <td className="px-2 sm:px-4 py-2 min-w-[100px] sm:py-3">
                          <span
                            className={`inline-flex items-center rounded-full text-xs font-medium px-2 py-0.5 ${hasCode
                                ? "bg-green-100 text-green-700"
                                : "bg-red-100 text-red-700"
                              }`}
                          >
                            {hasCode ? "Assigned" : "No code"}
                          </span>
                        </td>
                        <td className="px-2 sm:px-4 py-2 sm:py-3 text-right">
                          <PrimaryButton
                            label={hasCode ? "Edit" : "Assign"}
                            size="sm"
                            onClick={() => setEditingVoucher(voucher)}
                          />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          <div className="flex items-center justify-between mt-4">
            <p className="text-xs text-gray-500">Page {page}</p>
            <div className="flex gap-2">
              <PrimaryButton
                label="Previous"
                size="sm"
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page <= 1}
                className="!bg-white !text-dark !border-gray-border disabled:opacity-40"
              />
              <PrimaryButton
                label="Next"
                size="sm"
                onClick={() => setPage((p) => p + 1)}
                disabled={!hasNextPendingPage}
                className="!bg-droppie-oranje !border-droppie-oranje active:!bg-droppie-oranje-dark disabled:opacity-40"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default withProtectedRoute(
  withAdminPermission(PendingVoucherCodesScreen)
);
